import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { format, addDays } from "date-fns";
import { Share2, Calendar as CalIcon } from "lucide-react";
import { z } from "zod";
import { zodValidator, fallback } from "@tanstack/zod-adapter";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { DateField } from "@/components/calculator/DateField";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  daysBetween,
  countDays,
  monthsAndDays,
  liveCountdown,
  upcomingHolidays,
  type DayFilter,
} from "@/lib/calculators/daysCalculator";

type B = { en: string; bn: string };
type Lang = "en" | "bn";
type Mode = "between" | "until" | "since";

const T = {
  h1: { en: "Days Calculator", bn: "ডে ক্যালকুলেটর" },
  desc: {
    en: "Count days between two dates, days until a future date, or days since a past date — with Bangladesh holiday countdowns.",
    bn: "দুই তারিখের মধ্যে দিন, ভবিষ্যৎ তারিখ পর্যন্ত দিন, বা অতীত তারিখ থেকে দিন — বাংলাদেশের ছুটির কাউন্টডাউন সহ।",
  },
  mBetween: { en: "Days between", bn: "দুই তারিখের ব্যবধান" },
  mUntil: { en: "Days until", bn: "কত দিন বাকি" },
  mSince: { en: "Days since", bn: "কত দিন হলো" },
  start: { en: "Start date", bn: "শুরুর তারিখ" },
  end: { en: "End date", bn: "শেষের তারিখ" },
  target: { en: "Target date", bn: "লক্ষ্য তারিখ" },
  past: { en: "Past date", bn: "অতীত তারিখ" },
  includeEnd: { en: "Include end date", bn: "শেষ দিন অন্তর্ভুক্ত" },
  excludeHolidays: { en: "Exclude BD holidays", bn: "বাংলাদেশের ছুটি বাদ" },
  filterAll: { en: "All days", bn: "সব দিন" },
  filterWork: { en: "Working days", bn: "কর্মদিবস" },
  filterWeekend: { en: "Weekends only", bn: "শুধু ছুটির দিন" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  presets: { en: "Holiday countdowns", bn: "ছুটির কাউন্টডাউন" },
  share: { en: "Copy link", bn: "লিংক কপি" },
  copied: { en: "Copied!", bn: "কপি হয়েছে!" },
} as const;

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "How many days between two dates?", bn: "দুটি তারিখের মধ্যে কত দিন?" },
    a: {
      en: "Subtract the earlier date from the later date. We use date-fns differenceInCalendarDays which counts calendar boundaries crossed — Jan 1 to Jan 31 = 30 days (excluding Jan 1) or 31 (including end). Toggle 'Include end date' to choose.",
      bn: "পরের তারিখ থেকে আগের তারিখ বিয়োগ করুন। আমরা date-fns differenceInCalendarDays ব্যবহার করি — ১ জানু থেকে ৩১ জানু = ৩০ দিন (১ জানু বাদে) বা ৩১ (শেষ দিন সহ)। 'শেষ দিন অন্তর্ভুক্ত' টগল দিয়ে নির্বাচন করুন।",
    },
  },
  {
    q: { en: "How to count days excluding weekends?", bn: "ছুটির দিন বাদে দিন কীভাবে?" },
    a: {
      en: "Choose 'Working days' — we iterate every day in the range and skip Friday & Saturday (Bangladesh weekend). Optionally also exclude national holidays from the BD holiday list.",
      bn: "'কর্মদিবস' নির্বাচন করুন — আমরা প্রতিটি দিন গণনা করে শুক্র ও শনিবার (বাংলাদেশের সপ্তাহান্ত) বাদ দিই। জাতীয় ছুটিও বাদ দেওয়া যায়।",
    },
  },
  {
    q: { en: "দুটি তারিখের মধ্যে কত দিন?", bn: "দুটি তারিখের মধ্যে কত দিন?" },
    a: {
      en: "উপরের 'দুই তারিখের ব্যবধান' মোডে শুরু ও শেষ তারিখ বসিয়ে দিন। ফলাফলে দিন, সপ্তাহ ও মাস+দিন আকারে দেখানো হয়।",
      bn: "উপরের 'দুই তারিখের ব্যবধান' মোডে শুরু ও শেষ তারিখ বসিয়ে দিন। ফলাফলে দিন, সপ্তাহ ও মাস+দিন আকারে দেখানো হয়।",
    },
  },
  {
    q: { en: "Why does the countdown change every minute?", bn: "কাউন্টডাউন প্রতি মিনিটে কেন পরিবর্তন?" },
    a: {
      en: "The 'Days until' mode shows hours and minutes too, which tick down in real time. Days only change at midnight; hours and minutes refresh every minute on this page.",
      bn: "'কত দিন বাকি' মোডে ঘণ্টা ও মিনিটও দেখানো হয়, যা সরাসরি কমতে থাকে। দিন রাত ১২টায় বদলায়; ঘণ্টা/মিনিট প্রতি মিনিটে রিফ্রেশ হয়।",
    },
  },
  {
    q: { en: "Are holiday dates exact?", bn: "ছুটির তারিখ কি সঠিক?" },
    a: {
      en: "Fixed national holidays (Independence Day, Victory Day, Pohela Boishakh) are exact. Religious holidays (Eid, Shab-e-Barat) are approximate — they depend on moon sighting and may shift ±1 day per the official NBR notification.",
      bn: "নির্দিষ্ট জাতীয় ছুটি (স্বাধীনতা দিবস, বিজয় দিবস, পহেলা বৈশাখ) সঠিক। ধর্মীয় ছুটি (ঈদ, শবে বরাত) আনুমানিক — চাঁদ দেখার উপর নির্ভর করে এনবিআর-এর চূড়ান্ত নোটিফিকেশন অনুযায়ী ±১ দিন হতে পারে।",
    },
  },
];

const searchSchema = z.object({
  mode: fallback(z.enum(["between", "until", "since"]).optional(), "between").default("between"),
  d: fallback(z.string().optional(), "").default(""),  // single date for until/since
  s: fallback(z.string().optional(), "").default(""),  // start
  e: fallback(z.string().optional(), "").default(""),  // end
});

export const Route = createFileRoute("/days-calculator")({
  validateSearch: zodValidator(searchSchema),
  head: () => {
    const seo = buildSeo({
      title: "Days Between Dates Calculator — Day Counter",
      description:
        "Free days calculator. Count days between two dates, days until [future], days since [past]. Bangladesh public holiday countdowns. ডে কাউন্টার।",
      path: "/days-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Days Calculator", path: "/days-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: DaysCalculatorPage,
});

const parseISO = (s: string): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const toISO = (d: Date | null) => (d ? format(d, "yyyy-MM-dd") : "");

function DaysCalculatorPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmtN = (n: number) => num(n.toLocaleString("en-US"));
  const fmtDate = (d: Date) => {
    const s = format(d, "EEE, d MMM yyyy");
    return useBn ? toBengaliNumerals(s) : s;
  };

  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const today = useMemo(() => new Date(), []);
  const [mode, setMode] = useState<Mode>((search.mode ?? "between") as Mode);
  const [start, setStart] = useState<Date | null>(parseISO(search.s ?? "") ?? today);
  const [end, setEnd] = useState<Date | null>(parseISO(search.e ?? "") ?? addDays(today, 30));
  const [single, setSingle] = useState<Date | null>(parseISO(search.d ?? "") ?? addDays(today, mode === "since" ? -30 : 30));
  const [includeEnd, setIncludeEnd] = useState(false);
  const [filter, setFilter] = useState<DayFilter>("all");
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  const [copied, setCopied] = useState(false);

  // Sync mode/dates to URL search params (shareable countdown)
  useEffect(() => {
    navigate({
      search: () => ({
        mode,
        s: mode === "between" ? toISO(start) : "",
        e: mode === "between" ? toISO(end) : "",
        d: mode !== "between" ? toISO(single) : "",
      }),
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, start, end, single]);

  // Live tick for "until" mode
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (mode !== "until") return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [mode]);

  const between = useMemo(() => {
    if (!start || !end) return null;
    const total = daysBetween(start, end, { includeEnd });
    const md = monthsAndDays(start, end);
    const weeks = Math.floor(total / 7);
    const filtered = filter === "all" ? total : countDays(start, end, filter, "fri-sat", excludeHolidays);
    return { total, md, weeks, filtered };
  }, [start, end, includeEnd, filter, excludeHolidays]);

  const until = useMemo(() => {
    if (!single) return null;
    const cd = liveCountdown(single, now);
    return cd;
  }, [single, now]);

  const upcoming = useMemo(() => upcomingHolidays(today, 7), [today]);

  const copyLink = async () => {
    if (typeof window === "undefined") return;
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <CalculatorLayout
      slug="days-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="CalendarClock"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-border bg-card p-1 text-xs">
          {(["between", "until", "since"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                "rounded-full px-3 py-1.5 font-medium transition-colors",
                mode === m ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
                lang === "bn" && "font-bn",
              )}
            >
              {m === "between" ? tr("mBetween") : m === "until" ? tr("mUntil") : tr("mSince")}
            </button>
          ))}
        </div>
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      {/* Inputs */}
      {mode === "between" ? (
        <div className="grid gap-4 md:grid-cols-2">
          <DateField label={tr("start")} value={start} onChange={setStart} fromYear={1900} toDate={new Date(2100, 11, 31)} />
          <DateField label={tr("end")} value={end} onChange={setEnd} fromYear={1900} toDate={new Date(2100, 11, 31)} />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <DateField
            label={mode === "until" ? tr("target") : tr("past")}
            value={single}
            onChange={setSingle}
            fromYear={1900}
            toDate={new Date(2100, 11, 31)}
          />
          <div className="flex items-end">
            <button
              type="button"
              onClick={copyLink}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-medium hover:border-accent",
                lang === "bn" && "font-bn",
              )}
            >
              <Share2 size={14} /> {copied ? tr("copied") : tr("share")}
            </button>
          </div>
        </div>
      )}

      {mode === "between" && (
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          <label className={cn("inline-flex items-center gap-2", lang === "bn" && "font-bn")}>
            <input type="checkbox" checked={includeEnd} onChange={(e) => setIncludeEnd(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
            {tr("includeEnd")}
          </label>
          <div className="inline-flex rounded-full border border-border bg-card p-0.5">
            {(["all", "weekdays", "weekends"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={cn(
                  "rounded-full px-2.5 py-1 text-xs font-medium",
                  filter === f ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
                  lang === "bn" && "font-bn",
                )}
              >
                {f === "all" ? tr("filterAll") : f === "weekdays" ? tr("filterWork") : tr("filterWeekend")}
              </button>
            ))}
          </div>
          {filter === "weekdays" && (
            <label className={cn("inline-flex items-center gap-2", lang === "bn" && "font-bn")}>
              <input type="checkbox" checked={excludeHolidays} onChange={(e) => setExcludeHolidays(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
              {tr("excludeHolidays")}
            </label>
          )}
        </div>
      )}

      {/* Output */}
      <div className="mt-6">
        {mode === "between" && between && start && end && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="rounded-2xl border border-accent/40 bg-accent/5 p-6 text-center">
              <p className="text-5xl font-bold tabular-nums md:text-6xl">{fmtN(between.filtered)}</p>
              <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                {filter === "all"
                  ? lang === "en" ? "days" : "দিন"
                  : filter === "weekdays"
                    ? lang === "en" ? "working days" : "কর্মদিবস"
                    : lang === "en" ? "weekend days" : "ছুটির দিন"}
              </p>
              <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                {fmtDate(start)} → {fmtDate(end)}
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Stat label={lang === "en" ? "Total days" : "মোট দিন"} value={fmtN(between.total)} />
              <Stat label={lang === "en" ? "Weeks" : "সপ্তাহ"} value={fmtN(between.weeks)} />
              <Stat
                label={lang === "en" ? "Months / days" : "মাস / দিন"}
                value={`${num(between.md.months)} / ${num(between.md.days)}`}
              />
            </div>
          </motion.div>
        )}

        {(mode === "until" || mode === "since") && until && single && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="rounded-2xl border border-accent/40 bg-accent/5 p-6 text-center">
              <p className="text-5xl font-bold tabular-nums md:text-6xl">{fmtN(until.days)}</p>
              <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                {mode === "until"
                  ? lang === "en" ? `days until ${format(single, "EEE, d MMM yyyy")}` : `দিন বাকি — ${fmtDate(single)}`
                  : lang === "en" ? `days since ${format(single, "EEE, d MMM yyyy")}` : `দিন হলো — ${fmtDate(single)}`}
              </p>
              {mode === "until" && (
                <p className={cn("mt-3 text-sm font-medium tabular-nums text-foreground", lang === "bn" && "font-bn")}>
                  {fmtN(until.hours)} {lang === "en" ? "h" : "ঘ"} · {fmtN(until.minutes)} {lang === "en" ? "m" : "মি"}
                </p>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Stat label={lang === "en" ? "Weeks" : "সপ্তাহ"} value={`${fmtN(Math.floor(until.days / 7))}, ${fmtN(until.days % 7)}d`} />
              <Stat label={lang === "en" ? "Total hours" : "মোট ঘণ্টা"} value={fmtN(until.totalHours)} />
              <Stat label={lang === "en" ? "Direction" : "দিক"} value={until.future ? (lang === "en" ? "Future" : "ভবিষ্যৎ") : (lang === "en" ? "Past" : "অতীত")} />
            </div>
          </motion.div>
        )}
      </div>

      {/* Holiday countdowns */}
      <section className="mt-10">
        <h2 className={cn("mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
          <CalIcon size={14} /> {tr("presets")}
        </h2>
        <div className="flex flex-wrap gap-2">
          {upcoming.map((h) => {
            const d = new Date(h.date);
            const days = Math.max(0, Math.ceil((d.getTime() - today.getTime()) / 86_400_000));
            return (
              <button
                key={h.date + h.name.en}
                type="button"
                onClick={() => {
                  setMode("until");
                  setSingle(d);
                }}
                className={cn(
                  "rounded-full border border-border bg-card px-3 py-1.5 text-xs hover:border-accent hover:text-accent",
                  lang === "bn" && "font-bn",
                )}
              >
                {h.name[lang]} · <strong className="tabular-nums">{num(days)}</strong>{" "}
                <span className="text-muted-foreground">{lang === "en" ? "days" : "দিন"}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "How to count days between two dates" : "দিন কীভাবে গণনা হয়"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "Subtract the earlier calendar date from the later one. Standard counting excludes the start day — e.g., Jan 1 to Jan 31 = 30 days. Toggle 'Include end date' if you also want to count the final day (gives 31 instead)."
              : "পরের তারিখ থেকে আগের তারিখ বিয়োগ করুন। সাধারণ গণনায় শুরুর দিন বাদ — যেমন ১ জানু থেকে ৩১ জানু = ৩০ দিন। শেষ দিন গুনতে চাইলে 'শেষ দিন অন্তর্ভুক্ত' টগল করুন (তখন ৩১)।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Working days in Bangladesh" : "বাংলাদেশে কর্মদিবস"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "The Bangladesh government and most private offices follow a Sunday–Thursday work week. Friday and Saturday are weekly holidays. The 'Working days' filter excludes Fri & Sat; you can additionally exclude national & religious holidays."
              : "বাংলাদেশ সরকার ও অধিকাংশ বেসরকারি অফিস রবি–বৃহস্পতি কর্মসপ্তাহ অনুসরণ করে। শুক্র ও শনিবার সাপ্তাহিক ছুটি। 'কর্মদিবস' ফিল্টার শুক্র-শনি বাদ দেয়; চাইলে জাতীয় ও ধর্মীয় ছুটিও বাদ দেওয়া যায়।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Common use cases" : "সাধারণ ব্যবহার"} lang={lang}>
          <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "Project deadlines — count working days remaining" : "প্রজেক্ট ডেডলাইন — অবশিষ্ট কর্মদিবস"}</li>
            <li>{lang === "en" ? "Anniversaries & milestones — days since a memorable date" : "বার্ষিকী ও মাইলফলক — কোনো দিন থেকে কত দিন"}</li>
            <li>{lang === "en" ? "Pregnancy week tracking — count weeks from conception" : "প্রেগন্যান্সি ট্র্যাকিং — গর্ভধারণ থেকে সপ্তাহ"}</li>
            <li>{lang === "en" ? "Holiday countdowns — Eid, Pohela Boishakh, Independence Day" : "ছুটির কাউন্টডাউন — ঈদ, পহেলা বৈশাখ, স্বাধীনতা দিবস"}</li>
          </ul>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/date-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Date" : "তারিখ"}</Link>
            <Link to="/year-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Year" : "বছর"}</Link>
            <Link to="/age-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Age" : "বয়স"}</Link>
            <Link to="/time-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Time" : "সময়"}</Link>
          </div>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: Lang }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
